import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Sparkles, PenSquare, History, Settings, LogOut, LayoutDashboard, BarChart2, CreditCard, Bell, Plus, CheckCircle2, BarChart, TriangleAlert, Search, HelpCircle, User, Zap } from 'lucide-react';
import { Box, Typography, Stack, Avatar, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, IconButton, useTheme, Button as MuiButton, LinearProgress, Popover, Badge, CircularProgress } from '@mui/material';

export default function DashboardLayout() {
  const { user, profile, notifications, usage, logout, isLoading, markNotificationAsRead, markAllNotificationsAsRead } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  // Extract the current tab name from the path for the header
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentTab = pathParts.length > 1
    ? pathParts[1].charAt(0).toUpperCase() + pathParts[1].slice(1)
    : 'Dashboard';

  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  // Notification menu state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleOpenNotifications = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };
  const openNotifications = Boolean(anchorEl);
  const id = openNotifications ? 'notifications-popover' : undefined;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f8f9fa' }}>
        <CircularProgress />
      </Box>
    );
  }

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const usagePercent = usage.planLimit > 0 ? (usage.generationsUsed / usage.planLimit) * 100 : 0;
  const creditsRemaining = usage.planLimit - usage.generationsUsed;

  const handleMarkAllRead = async () => {
    await markAllNotificationsAsRead();
  };

  const handleNotificationClick = async (notificationId: string) => {
    await markNotificationAsRead(notificationId);
    // Optionally navigate if the notification has a link
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: 'background.default' }}>
      {/* Sidebar */}
      <Box
        component="aside"
        sx={{
          width: { xs: '100%', md: 256 },
          bgcolor: 'background.paper',
          borderRight: { md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ bgcolor: '#4f46e5', p: 0.5, borderRadius: 1.5, display: 'flex' }}>
            <Sparkles color="white" size={24} />
          </Box>
          <Typography variant="h6" fontWeight="bold" letterSpacing="-0.02em" sx={{ color: '#111827' }}>
            PostPilot AI
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, px: 2 }}>
          <List>
            <NavItem to="/dashboard" end icon={<LayoutDashboard size={20} />} label="Dashboard" />
            <NavItem to="/dashboard/generate" icon={<PenSquare size={20} />} label="Generate Post" />
            <NavItem to="/dashboard/history" icon={<History size={20} />} label="History" />
            <NavItem to="/dashboard/analytics" icon={<BarChart2 size={20} />} label="Analytics" />
            {/* <NavItem to="/dashboard/settings" icon={<Settings size={20} />} label="Settings" /> */}
            <NavItem to="/dashboard/billing" icon={<CreditCard size={20} />} label="Billing" />
          </List>
        </Box>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Usage Block */}
          <Box sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" fontWeight="bold" color="#64748b" letterSpacing="0.05em">USAGE</Typography>
              <Typography variant="caption" fontWeight="bold" color={creditsRemaining === 0 ? '#ef4444' : '#4f46e5'}>{usage.generationsUsed}/{usage.planLimit}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={usagePercent} sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: usagePercent >= 100 ? '#ef4444' : usagePercent > 75 ? '#f59e0b' : '#4f46e5', borderRadius: 3 } }} />
            {creditsRemaining === 0 ? (
              <Box
                component={NavLink}
                to="/dashboard/billing"
                sx={{
                  mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  p: 1, borderRadius: 2, cursor: 'pointer', textDecoration: 'none',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  '&:hover': { opacity: 0.9 }
                }}
              >
                <Zap size={14} color="white" />
                <Typography variant="caption" fontWeight="bold" color="white">Upgrade to Pro</Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="#64748b" sx={{ mt: 1, display: 'block', fontSize: '0.7rem' }}>
                {creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} remaining this cycle.
              </Typography>
            )}
          </Box>

          <Divider sx={{ borderColor: '#e2e8f0' }} />

          <Stack direction="row" alignItems="center" spacing={1.5} component={NavLink} to="/dashboard/profile" sx={{ px: 1, py: 1, textDecoration: 'none', borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: '#f1f5f9' }, '&.active': { bgcolor: 'rgba(79, 70, 229, 0.08)' } }}>
            <Avatar src={profile?.avatarUrl || undefined} sx={{ width: 40, height: 40, bgcolor: '#4f46e5', color: 'white', fontWeight: 'bold' }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={700} color="#111827" noWrap>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="#4f46e5" fontWeight={600} sx={{ display: 'block', mt: -0.5 }}>
                Professional Plan
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: '#f8f9fa' }}>
        {/* Top Header Navbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 3, md: 5 }, py: 2.5, borderBottom: 1, borderColor: '#e2e8f0', bgcolor: 'white' }}>

          <Typography variant="h6" fontWeight={800} color="#111827" sx={{ minWidth: 150 }}>
            {location.pathname === '/dashboard/profile' ? 'User Profile' : location.pathname.includes('/generate') ? 'Create Post' : location.pathname.includes('/history') ? 'Generation History' : currentTab}
          </Typography>

          <Box sx={{ flexGrow: 1, maxWidth: 480, mx: 4, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9', borderRadius: 2, height: 40, px: 2 }}>
              <Search size={16} color="#94a3b8" style={{ marginRight: 8 }} />
              <Box component="input" placeholder="Search history..." sx={{ border: 'none', bgcolor: 'transparent', outline: 'none', flexGrow: 1, color: '#334155', fontSize: '0.875rem', '&::placeholder': { color: '#94a3b8' } }} />
            </Box>
          </Box>

          <Stack direction="row" alignItems="center" spacing={3}>
            <IconButton
              size="small"
              onClick={handleOpenNotifications}
              sx={{ color: '#64748b', p: 0.5 }}
            >
              <Badge
                color="error"
                variant={unreadCount > 0 ? "dot" : "standard"}
                sx={{ '& .MuiBadge-badge': { width: 8, height: 8, borderRadius: '50%', right: 3, top: 4, border: '2px solid white' } }}
              >
                <Bell size={20} />
              </Badge>
            </IconButton>

            <Popover
              id={id}
              open={openNotifications}
              anchorEl={anchorEl}
              onClose={handleCloseNotifications}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  width: 360,
                  maxHeight: 480,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }
              }}
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#111827">Notifications</Typography>
                {unreadCount > 0 && (
                  <Typography component="button" onClick={handleMarkAllRead} variant="caption" fontWeight={700} color="#4f46e5" sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                    Mark all as read
                  </Typography>
                )}
              </Box>
              <Divider />
              <List disablePadding sx={{ overflow: 'auto', maxHeight: 380 }}>
                {notifications.length > 0 ? (
                  notifications.map(n => (
                    <NotificationItem
                      key={n.id}
                      id={n.id}
                      icon={n.type === 'success' ? <CheckCircle2 size={16} color="#10b981" /> : n.type === 'warning' ? <TriangleAlert size={16} color="#f59e0b" /> : <Bell size={16} color="#4f46e5" />}
                      iconBg={n.type === 'success' ? '#dcfce7' : n.type === 'warning' ? '#fef3c7' : '#e0e7ff'}
                      title={n.title}
                      message={n.message}
                      time={new Date(n.createdAt).toLocaleDateString()}
                      unread={!n.isRead}
                      onClick={() => handleNotificationClick(n.id)}
                    />
                  ))
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="#64748b">No notifications yet.</Typography>
                  </Box>
                )}
              </List>
              <Divider />
              <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography variant="body2" fontWeight={600} color="#475569" sx={{ cursor: 'pointer', '&:hover': { color: '#111827' } }}>
                  View all activity
                </Typography>
              </Box>
            </Popover>

            <Avatar
              onClick={handleOpenUserMenu}
              src={profile?.avatarUrl || undefined}
              sx={{ width: 36, height: 36, bgcolor: '#e0e7ff', color: '#4f46e5', fontSize: '0.875rem', fontWeight: 700, ml: 1, cursor: 'pointer' }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>

            <Popover
              open={Boolean(userMenuAnchor)}
              anchorEl={userMenuAnchor}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1.5, width: 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="body2" fontWeight={700} color="#111827" noWrap>{user?.name}</Typography>
                <Typography variant="caption" color="#64748b" noWrap display="block">{user?.email}</Typography>
              </Box>
              <List disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { navigate('/dashboard/profile'); handleCloseUserMenu(); }} sx={{ py: 1.2 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><User size={18} /></ListItemIcon>
                    <ListItemText primary="My Profile" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                  </ListItemButton>
                </ListItem>
                <Divider />
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout} sx={{ py: 1.2 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: 'error.main' }}><LogOut size={18} /></ListItemIcon>
                    <ListItemText primary="Sign Out" primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'error.main' }} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Popover>
          </Stack>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 3, md: 5 } }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function NavItem({ to, end, icon, label }: { to: string; end?: boolean; icon: React.ReactNode; label: string }) {
  return (
    <ListItem disablePadding sx={{ mb: 1 }}>
      <ListItemButton
        component={NavLink}
        to={to}
        end={end}
        sx={{
          borderRadius: 2,
          color: '#64748b',
          py: 1.25,
          px: 2,
          '&.active': {
            bgcolor: 'rgba(79, 70, 229, 0.08)',
            color: '#4f46e5',
            '& .MuiListItemIcon-root': { color: '#4f46e5' }
          },
          '&:hover:not(.active)': { bgcolor: '#f1f5f9', color: '#334155' }
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
      </ListItemButton>
    </ListItem>
  );
}

function NotificationItem({ icon, iconBg, title, message, time, unread, onClick }: any) {
  return (
    <ListItem disablePadding sx={{ borderBottom: '1px solid #f1f5f9', bgcolor: unread ? 'rgba(79, 70, 229, 0.02)' : 'transparent', '&:hover': { bgcolor: '#f8fafc' } }}>
      <ListItemButton onClick={onClick} sx={{ px: 2, py: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 1.5, mt: 0.5 }}>
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#111827" sx={{ lineHeight: 1.3 }}>{title}</Typography>
          <Typography variant="caption" color="#64748b" display="block" sx={{ mb: 0.5 }}>{message}</Typography>
          <Typography variant="caption" color="#94a3b8" fontWeight={500}>{time}</Typography>
        </Box>
        {unread && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4f46e5', mt: 1 }} />}
      </ListItemButton>
    </ListItem>
  );
}
